import type { FC, ReactNode } from 'react'
import type { SplitText as SplitTextInstance } from 'gsap/SplitText'

import { useRef, useState } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import CropMarks from '@/components/generals/crop-marks'
import SectionLabel from '@/components/generals/section-label'
import { gsap, prefersReducedMotion, SplitText, useGSAP } from '@/lib/motion'
import { testimonials } from '@/constants/content'

// Client words on the lime environment. Switching voices wipes the duotone
// portrait and sets the new quote rising word by word out of its lines.
const Testimonials: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const splitRef = useRef<SplitTextInstance | null>(null)
    const firstRef = useRef(true)
    const [slide, setSlide] = useState({ active: 0, previous: -1 })
    const count = testimonials.length

    useGSAP(() => {
        if (firstRef.current) {
            firstRef.current = false
            return
        }

        if (prefersReducedMotion()) return

        const incoming = sectionRef.current?.querySelector<HTMLElement>(`[data-quote="${slide.active}"] blockquote`)

        if (!incoming) return

        splitRef.current?.revert()

        const split = SplitText.create(incoming, { type: 'lines,words', mask: 'lines' })

        splitRef.current = split

        gsap.from(split.words, {
            yPercent: 115,
            duration: 1,
            ease: 'expo.out',
            stagger: 0.018,
            onComplete: () => {
                split.revert()

                if (splitRef.current === split) splitRef.current = null
            }
        })
    }, { dependencies: [slide.active], scope: sectionRef })

    const go = (next: number): void => {
        const target = (next + count) % count

        setSlide(state => state.active === target ? state : { active: target, previous: state.active })
    }

    const current = testimonials[slide.active]

    return (
        <section id='testimonials' ref={sectionRef} data-scene='volt' aria-labelledby='testimonials-title' className='px-5 py-28 md:px-8 md:py-40'>
            <div className='flex flex-wrap items-end justify-between gap-8'>
                <SectionLabel index='06'>
                    <span id='testimonials-title'>
                        In their words
                    </span>
                </SectionLabel>

                <div className='flex items-center gap-6'>
                    <p className='type-label tabular-nums'>
                        0{slide.active + 1} / 0{count}
                    </p>

                    <div className='flex'>
                        <button
                            type='button'
                            onClick={() => go(slide.active - 1)}
                            aria-label='Previous testimonial'
                            className='flex size-12 items-center justify-center border border-fore transition-colors duration-300 hover:bg-fore hover:text-canvas'
                        >
                            <ArrowLeft aria-hidden='true' className='size-5' />
                        </button>

                        <button
                            type='button'
                            onClick={() => go(slide.active + 1)}
                            aria-label='Next testimonial'
                            className='-ml-px flex size-12 items-center justify-center border border-fore transition-colors duration-300 hover:bg-fore hover:text-canvas'
                        >
                            <ArrowRight aria-hidden='true' className='size-5' />
                        </button>
                    </div>
                </div>
            </div>

            <div className='mt-14 grid gap-14 lg:mt-20 lg:grid-cols-12 lg:gap-10'>
                <div className='grid lg:col-span-8 [grid-template-areas:"stack"]' aria-live='polite'>
                    {testimonials.map((testimonial, index) => (
                        <figure
                            key={testimonial.name}
                            data-quote={index}
                            aria-hidden={index !== slide.active}
                            className={cn(
                                '[grid-area:stack] transition-[opacity,visibility] duration-500',
                                index === slide.active ? 'visible opacity-100' : 'invisible opacity-0'
                            )}
                        >
                            <blockquote className='font-heading text-[clamp(1.85rem,3.8vw,4.3rem)] font-[560] leading-[1.05] tracking-[-0.035em] [font-variation-settings:"wdth"_104]'>
                                <span aria-hidden='true' className='type-serif mr-[0.08em]'>“</span>
                                {testimonial.quote}
                                <span aria-hidden='true' className='type-serif ml-[0.04em]'>”</span>
                            </blockquote>

                            <figcaption className='mt-10 flex flex-wrap items-baseline gap-x-4 gap-y-1'>
                                <span className='text-lg font-semibold'>
                                    {testimonial.name}
                                </span>

                                <span className='type-label text-quiet'>
                                    {testimonial.role}, {testimonial.company}
                                </span>
                            </figcaption>
                        </figure>
                    ))}
                </div>

                <div className='lg:col-span-4'>
                    <div className='relative mx-auto aspect-[4/5] w-full max-w-sm lg:ml-auto lg:mr-0'>
                        <div className='absolute inset-0 overflow-hidden mix-blend-multiply contrast-125 grayscale'>
                            {testimonials.map((testimonial, index) => (
                                <img
                                    key={testimonial.name}
                                    src={testimonial.image}
                                    alt={index === slide.active ? testimonial.imageAlt : ''}
                                    loading='lazy'
                                    decoding='async'
                                    className={cn(
                                        'absolute inset-0 size-full object-cover',
                                        index === slide.active && 'z-[2] [clip-path:inset(0%_0%_0%_0%)] transition-[clip-path] duration-[900ms] ease-quart',
                                        index === slide.previous && index !== slide.active && 'z-[1]',
                                        index !== slide.active && index !== slide.previous && 'z-0 [clip-path:inset(100%_0%_0%_0%)]'
                                    )}
                                />
                            ))}
                        </div>

                        <CropMarks />
                    </div>

                    <div className='mt-8 flex flex-wrap gap-2 lg:justify-end'>
                        {testimonials.map((testimonial, index) => (
                            <button
                                key={testimonial.name}
                                type='button'
                                aria-pressed={index === slide.active}
                                onClick={() => go(index)}
                                className={cn(
                                    'type-label border border-fore px-3 py-2 transition-colors duration-300',
                                    index === slide.active ? 'bg-fore text-canvas' : 'hover:bg-fore/10'
                                )}
                            >
                                {testimonial.name}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <dl className='mt-16 grid grid-cols-3 border-t border-fore/25'>
                {current?.stats.map((stat, index) => (
                    <div key={`${current.name}-${stat.label}`} className={cn('pt-6', index > 0 && 'border-l border-fore/25 pl-4 md:pl-8')}>
                        <dt className='type-label text-quiet'>
                            {stat.label}
                        </dt>

                        <dd className='mt-3 font-heading text-[clamp(1.6rem,4vw,4.5rem)] font-[650] leading-none tracking-[-0.04em] tabular-nums'>
                            {stat.value}
                        </dd>
                    </div>
                ))}
            </dl>
        </section>
    )
}

export default Testimonials
